import { useEffect, useState } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { Card, Col, Row, Select, Spin } from "antd";
import {
  getCompanyCategoryUrl,
  getCompanyReviews,
} from "apis/competitorPulseApis";
import APIError from "components/Common/APIError";
import ComparisonBarChart from "./CompersionBarChart";
import { defaultColor } from "../constants";

const CompareReviewAnalysis = ({
  source,
  loading = false,
  onFilterChange,
  filterValue,
  barChartFilters,
  selectedColor = defaultColor,
  isCompareClicked,
  initialLoad,
}) => {
  const paginationSize = 1;
  const page_number = 1;
  const [categoryLists, setCategoryLists] = useState([[], []]);

  const categoryQueries = useQueries({
    queries: barChartFilters.companies.map((company) => ({
      queryKey: [
        "categoryCount",
        company?.company,
        company?.startDate,
        company?.endDate,
        filterValue.sourceList,
        company?.bikeModel,
        filterValue.sentiment,
      ],
      queryFn: () =>
        getCompanyCategoryUrl(
          company.company,
          Math.floor(company.startDate / 1000),
          Math.floor(company.endDate / 1000),
          filterValue.sourceList,
          company.bikeModel,
          filterValue.sentiment
        ),
      keepPreviousData: true,
      enabled:
        (initialLoad || isCompareClicked) &&
        !!(company?.company && company?.startDate && company?.endDate),
      refetchOnWindowFocus: false,
    })),
  });
  const [category1, category2] = categoryQueries;

  const categoryData1 = category1.data;
  const categoryDataLoading1 = category1.isLoading;
  const categoryDataIsError1 = category1.isError;
  const categoryDataError1 = category1.error;

  const categoryData2 = category2.data;
  const categoryDataLoading2 = category2.isLoading;
  const categoryDataIsError2 = category2.isError;
  const categoryDataError2 = category2.error;
  

  const reviewQueries = useQueries({
    queries: barChartFilters.companies.map((company) => ({
      queryKey: [
        "reviewDetails",
        company?.company,
        company?.startDate,
        company?.endDate,
        filterValue.sourceList,
        company?.bikeModel,
        filterValue.sentiment,
        page_number,
        paginationSize,
      ],
      queryFn: () =>
        getCompanyReviews(
          company.company,
          Math.floor(company.startDate / 1000),
          Math.floor(company.endDate / 1000),
          page_number,
          paginationSize,
          filterValue.sourceList,
          company.bikeModel,
          filterValue.sentiment
        ),
      keepPreviousData: true,
      enabled: !!(company?.company && company?.startDate && company?.endDate),
      refetchOnWindowFocus: false,
    })),
  });

  const [review1, review2] = reviewQueries;

  const reviewData1 = review1.data;
  const reviewData2 = review2.data;

  // Update category lists when data changes
  useEffect(() => {
    if (categoryData1?.category_percentage) {
      setCategoryLists((prev) => {
        const newLists = [...prev];
        newLists[0] = Object.entries(categoryData1.category_percentage).map(
          ([key, value], index) => ({
            id: index + 1,
            parent: null,
            name: key,
            value: value,
          })
        );
        return newLists;
      });
    } else {
      setCategoryLists((prev) => {
        const newLists = [...prev];
        newLists[0] = [];
        return newLists;
      });
    }
  }, [categoryData1]);

  useEffect(() => {
    if (categoryData2?.category_percentage) {
      setCategoryLists((prev) => {
        const newLists = [...prev];
        newLists[1] = Object.entries(categoryData2.category_percentage).map(
          ([key, value], index) => ({
            id: index + 1,
            parent: null,
            name: key,
            value: value,
          })
        );
        return newLists;
      });
    } else {
      setCategoryLists((prev) => {
        const newLists = [...prev];
        newLists[1] = [];
        return newLists;
      });
    }
  }, [categoryData2]);

  const hasError = categoryDataIsError1 || categoryDataIsError2;
  const isLoading = categoryDataLoading1 || categoryDataLoading2;
  const error = categoryDataError1 || categoryDataError2;

  const renderChartSection = () => {
    if (hasError) {
      return (
        <div
          className="comparison-chart-container"
          style={{ padding: "40px", textAlign: "center" }}
        >
          <APIError
            message={
              error?.response?.data?.message || "Failed to load comparison data"
            }
          />
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="comparison-chart-container">
          <Spin tip="Loading comparison data..." size="large" />
        </div>
      );
    }

    if (categoryLists.every((list) => list.length === 0)) {
      return (
        <div className="comparison-chart-container">
          No data available for comparison
        </div>
      );
    }

    return (
      <div className="comparison-chart-container">
        <ComparisonBarChart
          categoryList1={categoryLists[0]}
          categoryList2={categoryLists[1]}
          company1Name={barChartFilters?.companies?.[0]?.company}
          company2Name={barChartFilters?.companies?.[1]?.company}
          company1TotalReviews={reviewData1?.total_reviews || 0}
          company2TotalReviews={reviewData2?.total_reviews || 0}
          height={450}
        />
      </div>
    );
  };

  return (
    <Card
      title={
        <>
          <div className="sub-header">Company Comparison</div>
          <div className="card-sub-text">
            Compare companies against each other.
          </div>
        </>
      }
      bordered={false}
    >
      {/* Filters Row */}
      <Row gutter={[16, 16]} className="review-analysis-box">
        <Col xs={24} sm={12} md={6} lg={5} xl={4}>
          <div className="lable">Source</div>
          <Select
            allowClear
            size="large"
            mode="multiple"
            placeholder="Select a Source"
            className="w100 mt05"
            value={filterValue.sourceList || [null]}
            loading={loading}
            maxTagCount="responsive"
            onChange={(value) =>
              onFilterChange("sourceList", null, value, source?.sources.length)
            }
          >
            <Select.Option value={null}>All</Select.Option>
            {source?.sources?.map((item, index) => (
              <Select.Option key={index} value={item}>
                {item}
              </Select.Option>
            ))}
          </Select>
        </Col>

        <Col xs={24} sm={12} md={6} lg={5} xl={4}>
          <div className="lable">Sentiment</div>
          <Select
            allowClear
            size="large"
            placeholder="Select a sentiment"
            className="w100 mt05 text-capitalize"
            value={filterValue.sentiment || null}
            maxTagCount="responsive"
            onChange={(value) => onFilterChange("sentiment", null, value)}
          >
            <Select.Option value={null}>All</Select.Option>
            <Select.Option value="positive">Positive</Select.Option>
            <Select.Option value="negative">Negative</Select.Option>
          </Select>
        </Col>
      </Row>

      {/* Chart Section */}
      <div style={{ marginTop: "24px" }}>{renderChartSection()}</div>
    </Card>
  );
};

export default CompareReviewAnalysis;
